import { useMemo } from "react";
import {
  featuresSample_arsonStrong,
  featuresSample_naturalStrong,
  SCENARIO_MOCKED_PROB,
  similarCasesSample_arsonStrong,
  similarCasesSample_naturalStrong,
} from "@/data/mockupData";

// iter2.2: 지원 시나리오. URL ?scenario=arson-strong | natural-strong
export type DemoScenario = "arson-strong" | "natural-strong" | null;

import { useAnalysisFeatures, useSimilarCases } from "@/hooks/useAnalysis";
import {
  useMassSpectrum,
  useSpectrumMZRT,
  useSpectrumTIC,
} from "@/hooks/useSpectrumData";
import type { MassSpectrumData } from "@/types";
import type {
  FeatureData,
  MZRTData,
  SimilarCase,
  TICData,
} from "@/types/spectrum";
import {
  transformFeaturesResponse,
  transformSimilarCasesResponse,
} from "@/utils/spectrumTransformers";

interface UseMockupFallbackOptions {
  analysisId?: number;
  sampleId?: number;
  selectedRT?: number;
  /**
   * iter2.2: Demo Scenario Mode. URL `?scenario=arson-strong|natural-strong` 시
   * 시나리오별 mockup features/similarCases + mocked RoMSH prob 적용.
   */
  scenario?: DemoScenario;
  /**
   * MS 원본 결측(D19) 신호. backend가 romsh_status="not_applicable_no_ms"로
   * 명시 선언한 TIC-only 시료일 때 SampleAnalysisPage가 true를 전달한다.
   * true면 MZRT/MS를 mockup으로 채우지 않고 빈 배열로 둬 정직 표시한다.
   * (API 응답 형태 추측이 아니라 backend의 권위 신호를 따른다.)
   */
  msMissing?: boolean;
  /**
   * MZRT 표시 밀도 선택 시 상한 포인트 수. 미지정 시 서버 기본값(데시메이션) 사용.
   * "상세(전체 피크)" 선택 시 SampleAnalysisPage가 4000을 전달한다.
   */
  mzrtMaxPoints?: number;
}

interface UseMockupFallbackResult {
  features: FeatureData[];
  similarCases: SimilarCase[];
  ticData: TICData[];
  mzrtData: MZRTData[];
  massSpectrumData: MassSpectrumData[];
  isMsMissing: boolean;
  isLoading: boolean;
  /** iter2.2: scenario active 시 mocked RoMSH probability. null이면 실제 사용. */
  scenarioMockedProb: number | null;
  /** iter2.2: scenario active 여부 — banner 노출 트리거 */
  isScenarioActive: boolean;
}

export function useMockupFallback({
  analysisId,
  sampleId,
  selectedRT,
  scenario = null,
  msMissing = false,
  mzrtMaxPoints,
}: UseMockupFallbackOptions): UseMockupFallbackResult {
  const featuresQuery = useAnalysisFeatures(analysisId);
  const similarCasesQuery = useSimilarCases(analysisId);
  const ticQuery = useSpectrumTIC(sampleId);
  const mzrtQuery = useSpectrumMZRT(sampleId, analysisId, mzrtMaxPoints);
  const massSpectrumQuery = useMassSpectrum(sampleId, selectedRT);

  const isLoading =
    featuresQuery.isLoading ||
    similarCasesQuery.isLoading ||
    ticQuery.isLoading ||
    mzrtQuery.isLoading ||
    massSpectrumQuery.isLoading;

  const hasApiFeatures =
    !featuresQuery.isError &&
    featuresQuery.data &&
    featuresQuery.data.length > 0;

  const hasApiSimilarCases =
    !similarCasesQuery.isError &&
    similarCasesQuery.data &&
    similarCasesQuery.data.length > 0;

  const hasApiTIC =
    !ticQuery.isError && ticQuery.data && ticQuery.data.length > 0;

  const hasApiMZRT =
    !mzrtQuery.isError && mzrtQuery.data && mzrtQuery.data.length > 0;

  const hasApiMassSpectrum =
    !massSpectrumQuery.isError &&
    massSpectrumQuery.data &&
    massSpectrumQuery.data.length > 0;

  // MS 원본 결측(D19): backend가 romsh_status="not_applicable_no_ms"로 명시 선언한
  // 신호를 SampleAnalysisPage가 msMissing으로 전달. API 응답 형태(빈배열 vs 에러)를
  // 추측하지 않으므로, 일반 API 빈응답/장애 시의 mockup fallback과 충돌하지 않는다.
  const isMsMissing = msMissing;

  // iter2.2: scenario active 시 features/similarCases override (forensic 정직성 banner와 함께)
  const isScenarioActive = scenario !== null;
  const scenarioMockedProb = scenario ? SCENARIO_MOCKED_PROB[scenario] : null;

  const features = useMemo<FeatureData[]>(() => {
    if (scenario === "arson-strong") return featuresSample_arsonStrong;
    if (scenario === "natural-strong") return featuresSample_naturalStrong;
    if (hasApiFeatures && featuresQuery.data) {
      return transformFeaturesResponse(featuresQuery.data);
    }
    // 2026-08-31: 무언(암묵) mockup fallback 을 제거했다. 이전에는 API 가 0건이면
    // 하드코딩 12종(벤젠·알킬벤젠·경유 의심 5건 포함)이 점선과 함께 그려졌고,
    // 발동 조건이 넓었다 — 검출 0건으로 정상 완료된 분석·실패한 분석·**실행 중**
    // (PENDING/RUNNING) 분석 전부. 경고는 영문 'Mockup Mode' 알약 하나였다.
    // 수사 화면이 가짜 동정을 근거처럼 보여주는 것이므로(실데이터 원칙·SVD-212-04
    // 합성 금지 정신), API 가 안 주면 정직하게 빈 상태를 그린다.
    // scenario 데모는 별개 — 명시적 URL 파라미터 + 전면 배너로만 동작한다.
    return [];
  }, [scenario, hasApiFeatures, featuresQuery.data]);

  const similarCases = useMemo<SimilarCase[]>(() => {
    if (scenario === "arson-strong") return similarCasesSample_arsonStrong;
    if (scenario === "natural-strong") return similarCasesSample_naturalStrong;
    if (hasApiSimilarCases && similarCasesQuery.data) {
      return transformSimilarCasesResponse(similarCasesQuery.data);
    }
    // 2026-08-31: features 와 같은 이유로 무언 mockup fallback 제거 — 빈 상태가 정직하다.
    return [];
  }, [scenario, hasApiSimilarCases, similarCasesQuery.data]);

  // 데이터 출처 selector 제거 후: TIC/MZRT/MS는 분석의 실제 시료 API 데이터만 사용.
  // API에 데이터가 없으면(장애/MS 결측) mockup 정적데이터로 채우지 않고 빈 배열 —
  // 차트는 "데이터 없음"으로 정직 표시한다 (실데이터 원칙: 가짜로 메우지 않음).
  const ticData = useMemo<TICData[]>(() => {
    if (hasApiTIC && ticQuery.data) {
      return ticQuery.data as TICData[];
    }
    return [];
  }, [hasApiTIC, ticQuery.data]);

  const mzrtData = useMemo<MZRTData[]>(() => {
    if (hasApiMZRT && mzrtQuery.data) {
      return mzrtQuery.data as MZRTData[];
    }
    return [];
  }, [hasApiMZRT, mzrtQuery.data]);

  const massSpectrumData = useMemo<MassSpectrumData[]>(() => {
    if (hasApiMassSpectrum && massSpectrumQuery.data) {
      return massSpectrumQuery.data;
    }
    return [];
  }, [hasApiMassSpectrum, massSpectrumQuery.data]);

  return {
    features,
    similarCases,
    ticData,
    mzrtData,
    massSpectrumData,
    isMsMissing,
    isLoading,
    scenarioMockedProb,
    isScenarioActive,
  };
}
